/**
 * Product Card Management System - Moon Moon Agency v1.0
 * Handles variant selection and quantity controls for product cards
 * Uses event delegation pattern consistent with mm_main_product.js
 */

/*********************************************************************************************
 * ⚙️ CONFIGURATION
 */
const CARD_CONFIG = {
    selectors: {
        cardForm: '[data-type="add-to-cart-form"]',
        variantInput: '[data-card-variant-input]',
        linkedInput: '[data-card-linked-input]',
        compositionModalOpen: '.mm-card-composition-modal[open]',
        variantIdInput: '[data-variant-id-input]',
        quantityInput: '[data-card-qtty-input]',
        quantityIncrease: '[data-card-qtty-increase]',
        quantityDecrease: '[data-card-qtty-decrease]',
        submitButton: '.mm-product-cta',
        productCard: '.mm-product-card',
    },

};

/*********************************************************************************************
 * 📦 PRODUCT DATA MANAGEMENT
 */
const ProductCardData = {
    /**
     * Get product data from a specific card element
     * @param {HTMLElement} card - The card element containing the product data
     */
    getFromCard(card) {
        if (!card) {
            window.log('error', 'ProductCard', 'No card element provided');
            return null;
        }

        // Find the script tag within this specific card
        const scriptTag = card.querySelector('script[data-product-id]');
        if (!scriptTag) {
            window.log('error', 'ProductCard', 'No product data script found in card');
            return null;
        }

        try {
            const data = JSON.parse(scriptTag.textContent);

            // Validate required properties
            if (!data || typeof data.id === 'undefined' || !Array.isArray(data.variants)) {
                window.log('error', 'ProductCard', 'Invalid product data structure', { data });
                return null;
            }

            window.log('info', 'ProductCard', 'Product data loaded', {
                productId: data.id,
                variantsCount: data.variants.length
            });
            return data;
        } catch (e) {
            window.log('error', 'ProductCard', 'Failed to parse product data', { error: e.message });
            return null;
        }
    }
};

/*********************************************************************************************
 * 🔄 VARIANT MANAGEMENT
 */
const VariantManager = {
    findMatchingVariant(productData, selectedOptions) {
        if (!productData || !productData.variants) {
            window.log('error', 'ProductCard', 'Invalid product data');
            return null;
        }

        const matchedVariant = productData.variants.find(variant => {
            // Check each option position
            return Object.keys(selectedOptions).every(optionKey => {
                const selectedValue = selectedOptions[optionKey];
                const variantValue = variant[optionKey];
                return variantValue === selectedValue;
            });
        });

        if (!matchedVariant) {
            window.log('warn', 'ProductCard', 'No variant matched', {
                selectedOptions,
                availableVariants: productData.variants.map(v => ({
                    option1: v.option1,
                    option2: v.option2,
                    option3: v.option3
                }))
            });
        }

        return matchedVariant;
    },

    getSelectedOptions(form) {
        const options = {};
        const inputs = form.querySelectorAll(CARD_CONFIG.selectors.variantInput);

        window.log('info', 'ProductCard', 'Getting selected options', {
            totalInputs: inputs.length,
            formId: form.getAttribute('id')
        });

        inputs.forEach(input => {
            if (input.checked) {
                const position = input.dataset.optionPosition;
                const optionKey = `option${position}`;
                options[optionKey] = input.value;

                window.log('info', 'ProductCard', 'Found checked option', {
                    position,
                    optionKey,
                    value: input.value
                });
            }
        });

        return options;
    },

    updateVariantId(form, variantId) {
        const input = form.querySelector(CARD_CONFIG.selectors.variantIdInput);
        if (input) {
            const oldValue = input.value;
            input.value = variantId;
            window.log('info', 'ProductCard', 'Variant ID updated in hidden input', {
                oldValue,
                newValue: variantId,
                inputName: input.name,
                inputId: input.id
            });
            window.emit('product:variant-input-updated', { variantId, form });
        } else {
            window.log('error', 'ProductCard', 'Hidden variant input not found', {
                selector: CARD_CONFIG.selectors.variantIdInput
            });
        }
    },

    updateButton(form, variant) {
        const button = form.querySelector(CARD_CONFIG.selectors.submitButton);
        if (!button) return;

        // Use texts from data attributes (set by Liquid translations)
        const addText = button.dataset.addText;
        const soldOutText = button.dataset.soldOutText;

        if (!addText || !soldOutText) {
            window.log('warn', 'ProductCard', 'Button missing translation data attributes', {
                hasAddText: !!addText,
                hasSoldOutText: !!soldOutText
            });
            return;
        }

        if (variant.available) {
            button.disabled = false;
            button.classList.remove('mm-cursor-default');
            button.textContent = addText;
        } else {
            button.disabled = true;
            button.classList.add('mm-cursor-default');
            button.textContent = soldOutText;
        }
    },

    updateVariantImage(form, variant) {
        if (!variant.featured_image) {
            window.log('info', 'ProductCard', 'No featured image for variant', { variantId: variant.id });
            return;
        }

        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        // Find the main image in the card (not the hover image)
        const mainImage = card.querySelector('.mm-product-card-image:not(.mm-product-card-image-hover)');
        if (!mainImage) {
            window.log('warn', 'ProductCard', 'Main product image not found in card');
            return;
        }

        try {
            // featured_image is a URL string from the JSON data
            const imageUrl = typeof variant.featured_image === 'string'
                ? variant.featured_image
                : variant.featured_image.src;
            mainImage.src = imageUrl;
            mainImage.alt = variant.name || mainImage.alt;

            // Update srcset if present (for responsive images)
            if (mainImage.hasAttribute('srcset')) {
                // Generate responsive srcset for variant image
                const srcset = this.generateResponsiveSrcset(imageUrl);
                mainImage.srcset = srcset;
            }

            window.log('info', 'ProductCard', 'Variant image updated', {
                variantId: variant.id,
                imageUrl
            });
        } catch (error) {
            window.log('error', 'ProductCard', 'Failed to update variant image', {
                variantId: variant.id,
                error: error.message
            });
        }
    },

    generateResponsiveSrcset(originalUrl) {
        if (!originalUrl || typeof originalUrl !== 'string') {
            return '';
        }

        // Common responsive breakpoints
        const breakpoints = [200, 300, 400, 500, 600, 700, 800, 900, 1000];

        return breakpoints
            .map(width => `${this.getShopifyImageUrl(originalUrl, width)} ${width}w`)
            .join(', ');
    },

    getShopifyImageUrl(originalUrl, width) {
        if (!originalUrl || typeof originalUrl !== 'string') {
            return originalUrl;
        }

        try {
            // Remove any existing size parameters
            let cleanUrl = originalUrl.replace(/_\d+x\d*\./g, '.');

            // Handle URLs with query parameters (like v= version)
            const [baseUrl, queryString] = cleanUrl.split('?');

            // Add width parameter before file extension
            const sizedUrl = baseUrl.replace(/(\.[^.]*$)/, `_${width}x$1`);

            // Reattach query string if it exists
            return queryString ? `${sizedUrl}?${queryString}` : sizedUrl;
        } catch (error) {
            window.log('warn', 'ProductCard', 'Failed to resize image URL, using original', {
                originalUrl,
                error: error.message
            });
            return originalUrl;
        }
    },

    updateQuantityMax(form, variant) {
        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        const quantityInput = card.querySelector(CARD_CONFIG.selectors.quantityInput);
        if (!quantityInput) return;

        let maxQty = 9999;

        if (variant.inventory_management &&
            variant.inventory_policy !== 'continue' &&
            variant.inventory_quantity > 0) {
            maxQty = variant.inventory_quantity;
        }

        quantityInput.max = maxQty;
        quantityInput.dataset.variantInventory = maxQty;

        // Reset quantity if it exceeds max
        const currentQty = parseInt(quantityInput.value, 10);
        if (currentQty > maxQty) {
            quantityInput.value = maxQty;
        }

        // Update button states
        QuantityManager.updateButtonStates(quantityInput);
    },

    handleVariantChange(form) {
        // Find the card containing this form
        const card = form.closest(CARD_CONFIG.selectors.productCard);
        if (!card) {
            window.log('error', 'ProductCard', 'Cannot find product card for form');
            return;
        }

        // Get product data from this specific card
        const productData = ProductCardData.getFromCard(card);
        if (!productData) {
            window.log('warn', 'ProductCard', 'No product data for variant change');
            return;
        }

        const selectedOptions = this.getSelectedOptions(form);
        const matchedVariant = this.findMatchingVariant(productData, selectedOptions);

        if (matchedVariant) {
            this.updateVariantId(form, matchedVariant.id);
            this.updateButton(form, matchedVariant);
            this.updateQuantityMax(form, matchedVariant);
            this.updateVariantImage(form, matchedVariant);
            CompositionInfoManager.update(form, matchedVariant.dimensions, matchedVariant.preorder_message);
            CompositionInfoManager.updatePrice(form, matchedVariant.price, matchedVariant.compare_at_price);
            CompositionInfoManager.updateThumb(form, matchedVariant.featured_image);

            window.log('info', 'ProductCard', 'Variant changed successfully', {
                productId: productData.id,
                variantId: matchedVariant.id,
                available: matchedVariant.available,
                selectedOptions
            });
        } else {
            window.log('warn', 'ProductCard', 'No variant matched', {
                productId: productData.id,
                selectedOptions,
                availableVariantsCount: productData.variants.length
            });

            // Disable button if no variant matches
            this.updateButton(form, { available: false });
        }
    }
};

/*********************************************************************************************
 * 🔗 LINKED PRODUCTS (popup Composition)
 * Produits lies (fiches distinctes reliees par custom.variant_linked_products) : chaque
 * radio porte deja l'id de variante et ses donnees (pas dans le JSON de la carte, qui ne
 * contient que les variantes du produit courant). Ces donnees mettent la popup a jour
 * tout de suite, puis swap() la remplace par celle de la fiche choisie : ses propres
 * options (cache-domino blanc a finitions, noir sans), sinon on gardait celles de la
 * carte cliquee et des combinaisons inexistantes (noir mat).
 */
const LinkedProductManager = {
    config: {
        // Section MM-Card-Composition.liquid : l'API Section Rendering attend l'id en minuscules.
        sectionId: 'mm-card-composition',
        selectors: {
            card: '.mm-product-card',
            productData: 'script[data-product-id]',
            dialog: 'dialog',
            stylesheet: 'link[rel="stylesheet"]',
            checkedLinkedInput: '[data-card-linked-input]:checked',
            productIdInput: 'input[name="product-id"]',
        },
    },

    // Requete en cours par carte : un 2e choix rapide annule le 1er.
    _controllers: new WeakMap(),

    handleChange(form, input) {
        const data = input.dataset;
        const variant = {
            id: input.value,
            available: data.available === 'true',
            inventory_management: data.inventoryManagement,
            inventory_policy: data.inventoryPolicy,
            inventory_quantity: parseInt(data.inventoryQuantity, 10) || 0,
        };

        VariantManager.updateVariantId(form, variant.id);
        VariantManager.updateButton(form, variant);
        VariantManager.updateQuantityMax(form, variant);
        this.updateHeader(input, data);
        CompositionInfoManager.update(form, data.dimensions, data.preorder);
        CompositionInfoManager.updateThumb(form, data.image);

        window.log('info', 'ProductCard', 'Linked product selected', { variantId: variant.id });
        this.swap(form, input);
    },

    /**
     * Popup de la fiche liee rendue par la section MM-Card-Composition (carte complete),
     * dont on ne reprend que le contenu du <dialog> et le JSON des variantes. Le <dialog>
     * lui-meme est garde : il reste ouvert.
     */
    async swap(form, input) {
        const card = form.closest(this.config.selectors.card);
        const dialog = input.closest(this.config.selectors.dialog);
        const url = input.dataset.url;
        if (!card || !dialog || !url) return;

        this._controllers.get(card)?.abort();
        const controller = new AbortController();
        this._controllers.set(card, controller);

        try {
            const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}section_id=${this.config.sectionId}`, { signal: controller.signal });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            this._apply(card, form, dialog, await response.text());
        } catch (error) {
            if (error.name === 'AbortError') return;
            // La mise a jour immediate (handleChange) reste en place : id, prix, titre.
            window.log('error', 'ProductCard', 'Linked product swap failed', { url, error: error.message });
        }
    },

    _apply(card, form, dialog, html) {
        const { selectors } = this.config;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newCard = doc.querySelector(selectors.card);
        const newDialog = newCard?.querySelector(selectors.dialog);
        const newData = newCard?.querySelector(selectors.productData);
        const data = card.querySelector(selectors.productData);
        if (!newDialog || !newData || !data) return;

        // Id de la carte rendue (unique_key fixe) -> id de la carte d'origine : labels,
        // aria-labelledby, noms des radios et id de la popup restent coherents.
        const rename = (markup) => markup.replaceAll(newCard.dataset.cardId, card.dataset.cardId);

        // Feuilles chargees par la popup de la fiche liee (vignette, personnalisation)
        // et absentes de la page.
        newCard.querySelectorAll(selectors.stylesheet).forEach(link => {
            const href = link.getAttribute('href');
            if (!document.querySelector(`link[href="${href}"]`)) document.head.append(link.cloneNode());
        });

        const hadFocus = dialog.contains(document.activeElement);

        data.textContent = newData.textContent;
        data.dataset.productId = newData.dataset.productId;
        form.dataset.productId = newData.dataset.productId;
        const productIdInput = form.querySelector(selectors.productIdInput);
        if (productIdInput) productIdInput.value = newData.dataset.productId;

        dialog.className = newDialog.className;
        dialog.setAttribute('aria-labelledby', rename(newDialog.getAttribute('aria-labelledby')));
        if (newDialog.dataset.customizationsScript) {
            dialog.dataset.customizationsScript = newDialog.dataset.customizationsScript;
        } else {
            delete dialog.dataset.customizationsScript;
        }
        // Balisage rendu par le Liquid du theme (section MM-Card-Composition), pas une saisie.
        dialog.innerHTML = rename(newDialog.innerHTML);

        const variantId = newCard.querySelector(CARD_CONFIG.selectors.variantIdInput)?.value;
        if (variantId) VariantManager.updateVariantId(form, variantId);
        QuantityManager.updateButtonStates(dialog.querySelector(CARD_CONFIG.selectors.quantityInput));
        CompositionScriptLoader.load(dialog.id);

        // Le radio choisi a ete remplace : le focus clavier revient sur son successeur.
        if (hadFocus) dialog.querySelector(selectors.checkedLinkedInput)?.focus();

        window.log('info', 'ProductCard', 'Linked product popup swapped', { productId: newData.dataset.productId });
    },

    updateHeader(input, data) {
        const dialog = input.closest('dialog');
        if (!dialog) return;

        const title = dialog.querySelector('[id^="composition-title-"]');
        if (title) title.textContent = data.title;
        CompositionInfoManager.updatePrice(dialog, data.price, data.comparePrice);
    }
};

/*********************************************************************************************
 * ℹ️ COMPOSITION INFOS (popup Composition)
 * Dimensions de la variante et message de precommande, comme sur la fiche produit :
 * ils dependent de la variante (ou du produit lie) choisie.
 */
const CompositionInfoManager = {
    config: {
        selectors: {
            dimensions: '[data-composition-dimensions]',
            preorder: '[data-composition-preorder]',
            thumb: '[data-composition-thumb]',
            price: '[data-composition-price]',
            comparePrice: '[data-composition-compare-price]',
        },
        classes: {
            separator: 'mm-card-composition-modal__sep',
        },
    },

    updateThumb(form, imageUrl) {
        const img = form.querySelector(this.config.selectors.thumb + ' img');
        if (!img || !imageUrl) return;

        img.src = imageUrl;
        img.srcset = VariantManager.generateResponsiveSrcset(imageUrl);
    },

    update(form, dimensions, preorderMessage) {
        const dimensionsEl = form.querySelector(this.config.selectors.dimensions);
        if (dimensionsEl) {
            dimensionsEl.replaceChildren();
            if (dimensions) {
                const separator = document.createElement('span');
                separator.className = this.config.classes.separator;
                separator.setAttribute('aria-hidden', 'true');
                dimensionsEl.append(separator, dimensions);
            }
        }

        const preorderEl = form.querySelector(this.config.selectors.preorder);
        if (preorderEl) {
            preorderEl.textContent = preorderMessage || '';
            preorderEl.hidden = !preorderMessage;
        }
    },

    // Prix deja formates en Liquid (money_without_trailing_zeros). innerHTML : le filtre money
    // est enrobe par l'app de remise (<span class="wd_discount">), comme dans le prix rendu en
    // Liquid. Contenu issu du Liquid du theme, pas d'une saisie.
    updatePrice(root, price, comparePrice) {
        const priceEl = root.querySelector(this.config.selectors.price);
        const compareEl = root.querySelector(this.config.selectors.comparePrice);

        if (priceEl && price) {
            priceEl.innerHTML = price;
            priceEl.classList.toggle('mm-tertiary', !!comparePrice);
        }
        if (compareEl) {
            compareEl.innerHTML = comparePrice || '';
            compareEl.hidden = !comparePrice;
        }
    }
};

/*********************************************************************************************
 * ↕️ COMPOSITION TOGGLE (popup Composition, mobile a un seul choix)
 * "Choisir mon modele" replie / deplie la liste des choix, la barre du bas restant
 * visible. Toujours rouverte depliee.
 */
const CompositionToggleManager = {
    config: {
        selectors: {
            toggle: '[data-composition-toggle]',
            dialog: '.mm-card-composition-modal',
        },
        classes: {
            collapsed: 'mm-collapsed',
        },
    },

    toggle(button) {
        const dialog = button.closest(this.config.selectors.dialog);
        if (!dialog) return;

        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!isExpanded));
        dialog.classList.toggle(this.config.classes.collapsed, isExpanded);
    },

    reset(dialogId) {
        const dialog = document.getElementById(dialogId);
        if (!dialog?.matches(this.config.selectors.dialog)) return;

        dialog.classList.remove(this.config.classes.collapsed);
        dialog.querySelector(this.config.selectors.toggle)?.setAttribute('aria-expanded', 'true');
    }
};

/*********************************************************************************************
 * 📥 COMPOSITION SCRIPT LOADER (popup Composition)
 * mm_product_customizations.js n'est charge que par la section fiche produit. Sur une
 * grille, il est injecte a la 1re ouverture d'une popup personnalisable. Une seule
 * fois : il declare un `const` global, une 2e inclusion leverait une erreur. Son
 * listener submit est en capture : le charger apres mm_cart.js ne change rien a
 * l'ordre de traitement.
 */
const CompositionScriptLoader = {
    config: {
        selectors: {
            script: 'script[src*="mm_product_customizations"]',
        },
    },

    load(dialogId) {
        const dialog = document.getElementById(dialogId);
        this._loadConfigurateur(dialog);

        const src = dialog?.dataset.customizationsScript;
        if (!src || document.querySelector(this.config.selectors.script)) return;

        this._inject(src);
        window.log('info', 'ProductCard', 'Customizations script loaded', { src });
    },

    /**
     * Popup configurateur (mm-card-configurateur-modal) : mm_configurateur.js monte
     * tous les parcours presents a son chargement. Deja charge (fiche produit,
     * popup ouverte plus tot), il ne voit pas ceux des cartes ajoutees ensuite
     * (filtres, pagination) : on relance son init, idempotente par racine.
     */
    _loadConfigurateur(dialog) {
        const src = dialog?.dataset.configurateurScript;
        if (!src) return;

        if (document.querySelector('script[src*="mm_configurateur"]')) {
            window.ConfigurateurManager?.init();
            return;
        }

        this._inject(src);
        window.log('info', 'ProductCard', 'Configurateur script loaded', { src });
    },

    _inject(src) {
        const script = document.createElement('script');
        script.src = src;
        script.defer = true;
        document.head.appendChild(script);
    }
};

/*********************************************************************************************
 * 🔢 QUANTITY MANAGEMENT
 */
const QuantityManager = {
    updateButtonStates(input) {
        if (!input) return;

        const currentValue = parseInt(input.value, 10) || 1;
        const maxValue = parseInt(input.max, 10) || 9999;

        const card = input.closest(CARD_CONFIG.selectors.productCard);
        if (!card) return;

        // Find plus button within the same card
        const plusBtn = card.querySelector(CARD_CONFIG.selectors.quantityIncrease);

        // Update plus button state only (minus button always enabled)
        if (plusBtn) {
            if (currentValue >= maxValue) {
                plusBtn.setAttribute('disabled', '');
            } else {
                plusBtn.removeAttribute('disabled');
            }
        }
    },

    changeQuantity(delta, button) {
        // Find the card container first, then find input within that card
        const card = button.closest(CARD_CONFIG.selectors.productCard);
        if (!card) {
            window.log('warn', 'ProductCard', 'Product card not found', { button });
            return;
        }

        const input = card.querySelector(CARD_CONFIG.selectors.quantityInput);
        if (!input) {
            window.log('warn', 'ProductCard', 'Quantity input not found in card', { card });
            return;
        }

        const current = parseInt(input.value, 10) || 1;
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 9999;

        // Calculate new value with constraints (same logic as mm_main_product.js)
        const newValue = Math.max(min, Math.min(max, current + delta));

        if (newValue !== current) {
            input.value = newValue;

            // Dispatch change event for potential listeners (consistent with mm_main_product.js)
            input.dispatchEvent(new Event('change', { bubbles: true }));

            this.updateButtonStates(input);

            window.log('info', 'ProductCard', 'Quantity changed', {
                oldValue: current,
                newValue,
                delta
            });
        }
    },

    validateInput(input) {
        if (!input) return;

        let value = parseInt(input.value, 10);
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 9999;

        if (isNaN(value) || value < min) {
            value = min;
        } else if (value > max) {
            value = max;
        }

        input.value = value;
        this.updateButtonStates(input);
    }
};

/*********************************************************************************************
 * 📐 DESCRIPTION LINE-CLAMP SYNC
 * Au hover, .mm-product-card-description doit occuper le meme nombre de lignes que
 * .mm-product-card-title (1 ou 2 selon le produit) : un clamp fixe a 2 ferait
 * deborder la description sous un titre qui ne tient que sur 1 ligne.
 */
const DescriptionClampManager = {
    config: {
        selectors: {
            wrapper: '.mm-product-card-wrapper-title',
            title: '.mm-product-card-title',
        },
    },

    resizeBound: false,

    init() {
        const wrappers = document.querySelectorAll(this.config.selectors.wrapper);
        if (!wrappers.length) return;

        wrappers.forEach(wrapper => this.syncLines(wrapper));

        if (!this.resizeBound) {
            window.addEventListener('resize', window.debounce(() => {
                document.querySelectorAll(this.config.selectors.wrapper).forEach(wrapper => this.syncLines(wrapper));
            }, 200));

            // Le titre est en police custom (Alegreya Sans) : mesurer avant son
            // chargement fige un nombre de lignes calcule sur la police de fallback.
            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(() => {
                    document.querySelectorAll(this.config.selectors.wrapper).forEach(w => this.syncLines(w));
                });
            }

            this.resizeBound = true;
        }
    },

    syncLines(wrapper) {
        const title = wrapper.querySelector(this.config.selectors.title);
        if (!title) return;

        const lineHeight = parseFloat(getComputedStyle(title).lineHeight) || (parseFloat(getComputedStyle(title).fontSize) * 1.4);
        if (!lineHeight) return;

        const lines = Math.max(1, Math.round(title.offsetHeight / lineHeight));
        wrapper.style.setProperty('--mm-desc-lines', lines);
    }
};

/*********************************************************************************************
 * 🖼️ SLIDER IMAGES PRELOAD
 * Les slides du slider hover sont en loading="lazy" et hors zone visible (overflow clip) :
 * le navigateur ne les charge qu'une fois entrees dans le viewport, soit PENDANT la premiere
 * transition -> slide vide/transparente laissant voir l'image principale en dessous.
 * On bascule en eager des la premiere interaction avec la carte (hover, touch, focus).
 */
const SliderPreloadManager = {
    config: {
        selectors: {
            sliderImage: '.mm-product-card-slider img[loading="lazy"]',
        },
    },

    preload(e) {
        const card = e.target.closest?.(CARD_CONFIG.selectors.productCard);
        if (!card || card.dataset.sliderPreloaded) return;

        card.dataset.sliderPreloaded = 'true';
        card.querySelectorAll(this.config.selectors.sliderImage).forEach(img => {
            img.loading = 'eager';
        });
    }
};

/*********************************************************************************************
 * 🖼️ SLIDER HOVER - 2E IMAGE
 * Le slider hover recouvre l'image principale : demarre sur la meme photo, le hover ne
 * changeait rien a l'ecran. A l'entree du pointeur (desktop), on le place sans transition
 * sur l'autre photo : la 2e en mode Jour, la 1re en mode Nuit (la carte affiche deja la 2e).
 */
const SliderHoverManager = {
    config: {
        selectors: {
            slider: '.mm-product-card-slider',
            night: '[data-day-night="night"]',
            grid: '[data-grid-collection]',
        },
        // Duree du fondu Jour / Nuit, alignee sur la transition d'opacite des images de la carte
        fadeDuration: 400,
    },

    showAlternateImage(e) {
        if (e.pointerType !== 'mouse' || window.isMobile()) return;

        const card = e.target.closest?.(CARD_CONFIG.selectors.productCard);
        // pointerover remonte depuis chaque enfant : ne traiter que l'entree dans la carte
        if (!card || card.contains(e.relatedTarget)) return;

        const splide = card.querySelector(this.config.selectors.slider)?._splide;
        if (!splide) return;

        // La largeur de carte change sans resize de fenetre (switch 2/4/6 colonnes) :
        // Splide garde alors l'ancien translateX et affiche deux demi-photos. Son resize
        // ne recalcule que si la taille du slider a reellement change.
        splide.emit('resize');

        const target = card.closest(this.config.selectors.night) ? 0 : 1;
        this.jump(splide, target);
    },

    /**
     * Grille collection : sur mobile le slider est toujours affiche et couvre les images de la
     * carte, le mode Nuit (qui ne permute que ces images) restait sans effet. Au switch Jour / Nuit
     * ou de colonnes, chaque slider est recale (resize) puis place sur la photo du mode courant.
     * `fade` (switch Jour / Nuit) : sur mobile, le changement passe par un fondu (voir fadeTo).
     */
    syncGrid(fade = false) {
        const grid = document.querySelector(this.config.selectors.grid);
        if (!grid) return;

        const target = grid.matches(this.config.selectors.night) ? 1 : 0;
        const animate = fade && window.isMobile() && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        grid.querySelectorAll(this.config.selectors.slider).forEach(slider => {
            const splide = slider._splide;
            if (!splide) return;
            splide.emit('resize');
            // opacity inline : un fondu precedent est encore en cours (switch rapproches)
            if (animate && (splide.index !== target || slider.style.opacity)) this.fadeTo(slider, splide, target);
            else this.jump(splide, target);
        });
    },

    /**
     * Le slider, toujours affiche sur mobile, couvre les images de la carte, qui passent deja
     * d'une photo a l'autre en fondu (CSS). On l'efface en fondu pour les reveler, puis on le
     * recale sans animation et on le reaffiche d'un coup une fois la photo cible chargee : il
     * montre alors la meme photo que la carte dessous, le retour ne se voit pas.
     */
    async fadeTo(slider, splide, target) {
        const run = (slider._mmFadeRun || 0) + 1;
        slider._mmFadeRun = run;

        const img = splide.Components.Slides.getAt(target)?.slide.querySelector('img');
        if (img) img.loading = 'eager';

        slider.style.opacity = '0';
        await Promise.all([
            new Promise(resolve => setTimeout(resolve, this.config.fadeDuration)),
            img?.decode().catch(() => {}),
        ]);
        // Un switch plus recent a pris la main sur ce slider
        if (slider._mmFadeRun !== run) return;

        this.jump(splide, target);
        slider.style.transition = 'none';
        slider.style.opacity = '';
        slider.offsetWidth; // applique le retour a 1 avant de rendre la transition
        slider.style.transition = '';
    },

    jump(splide, target) {
        if (splide.index === target) return;

        // Mutation directe (pas le setter options, qui relance un refresh) : speed 0 = saut sans animation
        const { speed } = splide.options;
        splide.options.speed = 0;
        splide.go(target);
        splide.options.speed = speed;
    }
};

/*********************************************************************************************
 * 🎬 EVENT DELEGATION & INITIALIZATION
 */
const ProductCardManager = {
    initialized: false,

    init() {
        // Initialize button states for all quantity inputs
        const quantityInputs = document.querySelectorAll(CARD_CONFIG.selectors.quantityInput);
        quantityInputs.forEach(input => {
            QuantityManager.updateButtonStates(input);
        });

        DescriptionClampManager.init();

        // Only add event listeners once (use event delegation on document)
        if (!this.initialized) {
            this.setupEventListeners();
            this.initialized = true;
        }

        window.log('info', 'ProductCard', 'Product card manager initialized', {
            formsCount: document.querySelectorAll(CARD_CONFIG.selectors.cardForm).length,
            quantityInputsCount: quantityInputs.length
        });
    },

    setupEventListeners() {
        // Precharge les images du slider avant la premiere transition
        const preload = (e) => SliderPreloadManager.preload(e);
        document.addEventListener('pointerover', preload, { passive: true });
        document.addEventListener('touchstart', preload, { passive: true });
        document.addEventListener('focusin', preload);
        document.addEventListener('pointerover', (e) => SliderHoverManager.showAlternateImage(e), { passive: true });
        document.addEventListener('collection:day-night-change', () => SliderHoverManager.syncGrid(true));
        document.addEventListener('collection:grid-columns-change', () => SliderHoverManager.syncGrid());

        // Cartes ajoutees par le scroll infini ou re-rendues par un filtre : leurs sliders ne sont
        // pas montes (SplideManager ne tourne qu'au chargement), sans slider ni 2e image au hover.
        // SplideManager ignore ceux deja montes ; syncGrid les place sur la photo du mode Jour / Nuit.
        const initNewCards = () => {
            window.SplideManager?.init();
            SliderHoverManager.syncGrid();
            this.init();
        };
        document.addEventListener('collection:infinite-success', initNewCards);
        document.addEventListener('collection:filter-success', initNewCards);

        // Event delegation for quantity buttons (MUST be before stopPropagation listener)
        document.addEventListener('click', (e) => {
            // Handle decrease buttons
            const decreaseBtn = e.target.closest(CARD_CONFIG.selectors.quantityDecrease);
            if (decreaseBtn) {
                e.preventDefault();
                e.stopPropagation();
                window.log('info', 'ProductCard', 'Decrease button clicked', { button: decreaseBtn });
                QuantityManager.changeQuantity(-1, decreaseBtn);
                return;
            }

            // Handle increase buttons
            const increaseBtn = e.target.closest(CARD_CONFIG.selectors.quantityIncrease);
            if (increaseBtn) {
                e.preventDefault();
                e.stopPropagation();
                window.log('info', 'ProductCard', 'Increase button clicked', { button: increaseBtn });
                QuantityManager.changeQuantity(1, increaseBtn);
                return;
            }

        });

        // Prevent clicks on variant/quantity selectors from triggering parent links
        document.addEventListener('click', (e) => {
            const clickedLink = e.target.closest('a');
            if (clickedLink) return;

            const variantLabel = e.target.closest('.mm-card-variant-option');
            if (variantLabel) {
                e.stopPropagation();
                return;
            }
        });

        // Event delegation for variant changes
        document.addEventListener('change', (e) => {
            const linkedInput = e.target.closest(CARD_CONFIG.selectors.linkedInput);
            if (linkedInput) {
                const form = linkedInput.closest(CARD_CONFIG.selectors.cardForm);
                if (!form) return;

                form.querySelectorAll(`input[name="${linkedInput.name}"]`).forEach(input => {
                    input.nextElementSibling?.classList.toggle('mm-selected', input.checked);
                });

                LinkedProductManager.handleChange(form, linkedInput);
                return;
            }

            const variantInput = e.target.closest(CARD_CONFIG.selectors.variantInput);
            if (variantInput) {
                const form = variantInput.closest(CARD_CONFIG.selectors.cardForm);
                if (form) {
                    // Update visual state
                    const optionName = variantInput.name;
                    const allInputsInGroup = form.querySelectorAll(`input[name="${optionName}"]`);

                    allInputsInGroup.forEach(input => {
                        const label = input.nextElementSibling;
                        if (label && label.tagName === 'LABEL') {
                            if (input.checked) {
                                label.classList.add('mm-selected');
                            } else {
                                label.classList.remove('mm-selected');
                            }
                        }
                    });

                    VariantManager.handleVariantChange(form);
                } else {
                    window.log('error', 'ProductCard', 'Form not found for variant input');
                }
            }
        });

        // Popup Composition : bouton "Choisir mon modele" (mobile, un seul choix)
        document.addEventListener('click', (e) => {
            const toggle = e.target.closest(CompositionToggleManager.config.selectors.toggle);
            if (toggle) CompositionToggleManager.toggle(toggle);
        });
        document.addEventListener('product:drawer-opened', (e) => {
            CompositionToggleManager.reset(e.detail?.id);
            CompositionScriptLoader.load(e.detail?.id);
        });

        // Popup Composition : fermee apres un ajout reussi, pour laisser place au panier
        // qui s'ouvre ensuite. Emis par mm_cart.js (flux standard) comme par
        // mm_product_customizations.js (personnalisation), avant l'ouverture du panier.
        document.addEventListener('cart:add-to-cart-success', () => {
            document.querySelectorAll(CARD_CONFIG.selectors.compositionModalOpen).forEach(dialog => dialog.close());
        });

        // Event delegation for quantity input changes (sanitize and validate)
        document.addEventListener('input', (e) => {
            const quantityInput = e.target.closest(CARD_CONFIG.selectors.quantityInput);
            if (quantityInput) {
                // Remove non-numeric characters first
                quantityInput.value = quantityInput.value.replace(/[^0-9]/g, '');

                // Then validate and clamp value
                QuantityManager.validateInput(quantityInput);
            }
        });
    }
};

/*********************************************************************************************
 * 🚀 AUTO-INIT
 */
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        ProductCardManager.init();
    });
} else {
    ProductCardManager.init();
}

// Shopify sections: Reinitialize when sections are loaded/reloaded
document.addEventListener('shopify:section:load', (event) => {
    window.log('info', 'ProductCard', 'Shopify section loaded', {
        sectionId: event.detail?.sectionId
    });
    ProductCardManager.init();
});

// Shopify sections: Reinitialize when blocks are loaded
document.addEventListener('shopify:block:select', (event) => {
    window.log('info', 'ProductCard', 'Shopify block selected', {
        blockId: event.detail?.blockId
    });
    ProductCardManager.init();
});

// Export for external use
window.ProductCardManager = ProductCardManager;
